/**
 * 10 procedural rave compositions defined as step-sequencer data.
 *
 * Each composition is a 16- or 32-step pattern at 128 BPM (16th notes).
 * MIDI note reference: C3=48, E3=52, G3=55, A3=57, C4=60, D4=62, E4=64, G4=67
 */

export interface Composition {
  name: string;
  bpm: number;
  steps: number;
  kick: boolean[];
  hat: boolean[];
  clap: boolean[];
  bass: (number | null)[];
  lead: (number | null)[];
  bassWaveform: OscillatorType;
  leadWaveform: OscillatorType;
  filterFreq: number;
  filterRes: number;
  filterEnvAmount?: number;
  kickPunch?: number;
  hatTone?: number;
  hatVol?: number;
  bassVol?: number;
  leadVol?: number;
  leadFilter?: number;
  leadSustain?: number;
}

const _ = null; // shorthand for rest

// Helper: repeat array to fill length
function rep<T>(arr: T[], len: number): T[] {
  const out: T[] = [];
  while (out.length < len) out.push(...arr);
  return out.slice(0, len);
}

export const compositions: Composition[] = [
  // 1. Classic four-on-the-floor techno
  {
    name: 'Warehouse Techno',
    bpm: 128,
    steps: 16,
    kick: [true,false,false,false, true,false,false,false, true,false,false,false, true,false,false,false],
    hat:  [false,false,true,false, false,false,true,false, false,false,true,false, false,false,true,false],
    clap: [false,false,false,false, true,false,false,false, false,false,false,false, true,false,false,false],
    bass: [36,_,_,36, _,_,36,_, 36,_,_,36, _,_,38,_],
    lead: [_,_,_,_, 60,_,63,_, _,_,_,_, 67,_,65,_],
    bassWaveform: 'sawtooth',
    leadWaveform: 'square',
    filterFreq: 400,
    filterRes: 8,
    filterEnvAmount: 600,
    bassVol: 0.3,
    leadVol: 0.08,
    leadFilter: 2500,
  },

  // 2. Acid house - 303 squelch
  {
    name: 'Acid Warehouse',
    bpm: 128,
    steps: 16,
    kick: [true,false,false,false, true,false,false,false, true,false,false,false, true,false,false,false],
    hat:  [false,false,true,false, false,false,true,false, false,false,true,true, false,false,true,false],
    clap: [false,false,false,false, true,false,false,false, false,false,false,false, true,false,false,true],
    bass: [36,_,36,_, 38,_,_,36, _,36,_,_, 41,_,38,_],
    lead: [_,_,_,_, _,_,_,_, _,_,_,_, _,_,_,_],
    bassWaveform: 'sawtooth',
    leadWaveform: 'square',
    filterFreq: 300,
    filterRes: 15,
    filterEnvAmount: 2000,
    bassVol: 0.35,
    hatVol: 0.25,
  },

  // 3. Happy hardcore
  {
    name: 'Happy Hardcore',
    bpm: 170,
    steps: 16,
    kick: [true,false,true,false, true,false,true,false, true,false,true,false, true,false,true,false],
    hat:  [true,true,true,true, true,true,true,true, true,true,true,true, true,true,true,true],
    clap: [false,false,false,false, true,false,false,false, false,false,false,false, true,false,false,false],
    bass: [48,_,_,48, _,_,48,_, 48,_,_,48, _,_,51,_],
    lead: [72,_,75,_, 79,_,75,_, 72,_,_,_, 75,_,72,_],
    bassWaveform: 'square',
    leadWaveform: 'sawtooth',
    filterFreq: 800,
    filterRes: 4,
    kickPunch: 200,
    bassVol: 0.25,
    leadVol: 0.1,
    leadFilter: 4000,
    hatVol: 0.15,
    hatTone: 9000,
  },

  // 4. Breakbeat
  {
    name: 'Breakbeat Madness',
    bpm: 132,
    steps: 16,
    kick: [true,false,false,false, false,false,true,false, false,true,false,false, false,false,false,false],
    hat:  [true,false,true,true, false,true,false,true, true,false,true,false, true,true,false,true],
    clap: [false,false,false,false, true,false,false,false, false,false,false,false, true,false,true,false],
    bass: [36,_,_,_, _,_,36,_, _,36,_,_, _,_,_,38],
    lead: [_,_,60,_, _,_,_,_, 63,_,_,_, _,65,_,_],
    bassWaveform: 'sawtooth',
    leadWaveform: 'square',
    filterFreq: 500,
    filterRes: 6,
    filterEnvAmount: 400,
    bassVol: 0.3,
    leadVol: 0.07,
    leadFilter: 2000,
  },

  // 5. Trance anthem
  {
    name: 'Trance Gates',
    bpm: 138,
    steps: 32,
    kick: rep([true,false,false,false, true,false,false,false, true,false,false,false, true,false,false,false], 32),
    hat:  rep([false,false,true,false, false,false,true,false, false,false,true,false, false,false,true,false], 32),
    clap: rep([false,false,false,false, true,false,false,false, false,false,false,false, true,false,false,false], 32),
    bass: [45,_,45,_, _,_,45,_, 45,_,_,_, _,_,45,_,  45,_,45,_, _,_,47,_, 48,_,_,_, _,_,47,_],
    lead: [69,_,69,_, 72,_,69,_, _,_,69,_, 72,_,76,_,  69,_,69,_, 74,_,72,_, _,_,69,_, 67,_,69,_],
    bassWaveform: 'sawtooth',
    leadWaveform: 'sawtooth',
    filterFreq: 600,
    filterRes: 6,
    filterEnvAmount: 800,
    bassVol: 0.25,
    leadVol: 0.1,
    leadFilter: 3500,
    leadSustain: 0.4,
  },

  // 6. Minimal techno
  {
    name: 'Minimal Pulse',
    bpm: 124,
    steps: 32,
    kick: rep([true,false,false,false, true,false,false,false, true,false,false,false, true,false,false,false], 32),
    hat:  [false,false,false,true, false,false,true,false, false,false,false,true, false,false,true,false, false,false,true,false, false,false,false,true, false,false,true,false, false,false,false,false],
    clap: rep([false,false,false,false, true,false,false,false, false,false,false,false, true,false,false,false], 32),
    bass: [36,_,_,_, _,_,_,_, 36,_,_,_, _,_,_,_,  38,_,_,_, _,_,_,_, 36,_,_,_, _,_,_,_],
    lead: [_,_,_,_, _,_,60,_, _,_,_,_, _,_,_,_,  _,_,_,_, 63,_,_,_, _,_,_,_, _,_,60,_],
    bassWaveform: 'sine',
    leadWaveform: 'triangle',
    filterFreq: 350,
    filterRes: 10,
    filterEnvAmount: 300,
    bassVol: 0.4,
    leadVol: 0.06,
    leadFilter: 1800,
    hatVol: 0.2,
  },

  // 7. Gabber / hard techno
  {
    name: 'Gabber Stomp',
    bpm: 160,
    steps: 16,
    kick: [true,false,false,false, true,false,false,false, true,false,false,false, true,false,false,false],
    hat:  [false,true,false,true, false,true,false,true, false,true,false,true, false,true,false,true],
    clap: [false,false,false,false, true,false,false,true, false,false,false,false, true,false,false,true],
    bass: [30,_,30,_, _,_,30,_, 30,_,_,30, _,_,33,_],
    lead: [_,_,_,_, _,_,_,_, 66,_,66,_, _,_,_,_],
    bassWaveform: 'sawtooth',
    leadWaveform: 'sawtooth',
    filterFreq: 250,
    filterRes: 12,
    filterEnvAmount: 1500,
    kickPunch: 200,
    bassVol: 0.35,
    leadVol: 0.09,
    leadFilter: 2000,
    hatTone: 6000,
    hatVol: 0.2,
  },

  // 8. Detroit techno
  {
    name: 'Detroit Groove',
    bpm: 128,
    steps: 16,
    kick: [true,false,false,false, true,false,false,false, true,false,false,false, true,false,false,false],
    hat:  [false,false,true,false, false,true,true,false, false,false,true,false, false,true,true,false],
    clap: [false,false,false,false, true,false,false,false, false,false,false,false, true,false,false,false],
    bass: [40,_,_,_, _,_,40,_, _,_,43,_, _,_,_,40],
    lead: [64,_,_,67, _,_,_,_, 71,_,_,_, _,69,_,_],
    bassWaveform: 'square',
    leadWaveform: 'triangle',
    filterFreq: 500,
    filterRes: 5,
    filterEnvAmount: 400,
    bassVol: 0.3,
    leadVol: 0.1,
    leadFilter: 3000,
    leadSustain: 0.8,
  },

  // 9. Progressive house
  {
    name: 'Progressive Pulse',
    bpm: 126,
    steps: 32,
    kick: rep([true,false,false,false, true,false,false,false, true,false,false,false, true,false,false,false], 32),
    hat:  rep([false,false,true,false, false,false,true,false, false,false,true,false, false,false,true,true], 32),
    clap: rep([false,false,false,false, true,false,false,false, false,false,false,false, true,false,false,false], 32),
    bass: [45,_,_,_, _,_,_,_, 45,_,_,_, _,_,_,_,  43,_,_,_, _,_,_,_, 43,_,_,_, _,_,45,_],
    lead: [_,_,_,_, 69,_,_,_, _,_,72,_, _,_,_,_,  _,_,_,_, 69,_,_,_, _,_,67,_, _,_,_,_],
    bassWaveform: 'sine',
    leadWaveform: 'sawtooth',
    filterFreq: 450,
    filterRes: 4,
    filterEnvAmount: 200,
    bassVol: 0.35,
    leadVol: 0.07,
    leadFilter: 2500,
    leadSustain: 0.9,
    hatVol: 0.2,
  },

  // 10. Drum & bass influenced
  {
    name: 'Jungle Pressure',
    bpm: 174,
    steps: 32,
    kick: [true,false,false,false, false,false,false,false, false,false,true,false, false,false,false,false, true,false,false,false, false,false,false,false, false,false,true,false, false,true,false,false],
    hat:  rep([true,false,true,true, false,true,true,false, true,false,true,false, true,true,false,true], 32),
    clap: [false,false,false,false, true,false,false,false, false,false,false,false, false,false,true,false, false,false,false,false, true,false,false,false, false,false,false,false, false,false,true,false],
    bass: [33,_,_,_, _,_,33,_, _,_,_,_, _,_,_,33,  35,_,_,_, _,_,35,_, _,_,_,_, _,_,33,_],
    lead: [_,_,_,_, _,_,_,_, 69,_,_,_, _,_,_,_,  _,_,_,_, _,_,_,_, 72,_,69,_, _,_,_,_],
    bassWaveform: 'sawtooth',
    leadWaveform: 'square',
    filterFreq: 350,
    filterRes: 10,
    filterEnvAmount: 800,
    bassVol: 0.35,
    leadVol: 0.06,
    leadFilter: 1800,
    hatVol: 0.15,
    hatTone: 8000,
  },
];
